// api.ts — client for the recipe API.
//
// Recipes are user-authored and owned by the authz identity that created them:
// anyone may read the catalogue, only the owner may edit or delete. The server
// is the authority on that — `canEdit` on each recipe says whether the current
// session owns it, and the UI uses it to decide which controls to show. Never
// infer permission client-side; the server re-checks every write.
//
// This replaces the old compiled-in recipes array. The human-curation rule is
// unchanged in spirit: a person still writes and verifies every recipe, they
// just do it in the app instead of in a TypeScript file.

export type Difficulty = 'beginner' | 'intermediate' | 'advanced' | 'expert'
export type CalloutVariant = 'note' | 'warning' | 'tip' | 'caution'

export interface Ingredient {
    name: string
    massG: number
    notes?: string
    optional?: boolean
}

export interface Callout {
    variant: CalloutVariant
    text: string
}

// StepUse is one ingredient a step consumes, as a share of the total the recipe
// lists for it rather than as a mass of its own.
//
// A share, so that the method and the ingredient table cannot disagree: correct
// the flour to 260 g on its row and every step that takes half the flour
// follows. It also makes the arithmetic checkable — one ingredient's shares
// across every step add up to at most 1.
export interface StepUse {
    /** Index into Recipe.ingredients. */
    ingredient: number
    /** Share of that ingredient's mass: 1 for all of it, 0.5 for half. */
    portion: number
}

export interface Step {
    number: number
    instruction: string
    durationSec?: number
    temperatureC?: number
    callout?: Callout
    /** What this step takes off the ingredient list. Absent on a step that
     *  handles no ingredient, and on every recipe saved before amounts were
     *  recorded per step. */
    uses?: StepUse[]
}

export interface Recipe {
    id: string
    ownerEmail: string
    title: string
    subtitle: string
    version: string
    difficulty: Difficulty
    category: string
    yieldDescription: string
    totalTimeMin: number
    prepTimeMin: number
    cookTimeMin: number
    equipment: string[]
    ingredients: Ingredient[]
    steps: Step[]
    notes: Callout[]
    sourceUrl?: string
    createdAt: string
    updatedAt: string
    canEdit: boolean
}

// emptyRecipe is the starting point for the create form.
export function emptyRecipe(): Recipe {
    return {
        id: '', ownerEmail: '', title: '', subtitle: '', version: '1.0.0',
        difficulty: 'intermediate', category: '', yieldDescription: '',
        totalTimeMin: 0, prepTimeMin: 0, cookTimeMin: 0,
        equipment: [], ingredients: [], steps: [], notes: [],
        createdAt: '', updatedAt: '', canEdit: true,
    }
}

// totalMass sums the ingredient masses of a recipe.
export function totalMass(recipe: Pick<Recipe, 'ingredients'>): number {
    return recipe.ingredients.reduce((sum, i) => sum + (i.massG || 0), 0)
}

// ApiError carries the server's message so the UI can show why a save failed
// (a slug conflict and a permission denial need different words).
export class ApiError extends Error {
    status: number
    constructor(status: number, message: string) {
        super(message)
        this.status = status
        this.name = 'ApiError'
    }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(path, {
        ...init,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    })
    if (res.status === 204) return undefined as T
    const text = await res.text()
    if (!res.ok) {
        let msg = text || res.statusText
        try {
            const parsed = JSON.parse(text)
            if (parsed?.error) msg = parsed.error
        } catch { /* not JSON — use the raw body */ }
        throw new ApiError(res.status, msg)
    }
    return text ? (JSON.parse(text) as T) : (undefined as T)
}

export function listRecipes(mine = false): Promise<Recipe[]> {
    return request<Recipe[]>(`/api/recipes${mine ? '?mine=true' : ''}`)
}

export function getRecipe(slug: string): Promise<Recipe> {
    return request<Recipe>(`/api/recipes/${encodeURIComponent(slug)}`)
}

export function createRecipe(r: Recipe): Promise<Recipe> {
    return request<Recipe>('/api/recipes', { method: 'POST', body: JSON.stringify(r) })
}

export function updateRecipe(slug: string, r: Recipe): Promise<Recipe> {
    return request<Recipe>(`/api/recipes/${encodeURIComponent(slug)}`, {
        method: 'PUT', body: JSON.stringify(r),
    })
}

export function deleteRecipe(slug: string): Promise<void> {
    return request<void>(`/api/recipes/${encodeURIComponent(slug)}`, { method: 'DELETE' })
}

// ImportMethod says where a draft's *quantities* came from, which is the part
// that decides how far to trust it.
//
//   structured-data  the page published schema.org recipe markup, and every
//                    mass is the author's own figure, read exactly. The method
//                    is a different matter: markup carries whatever prose the
//                    author typed and never says which step uses what, so the
//                    steps are rewritten and their amounts worked out by a
//                    model. No mass comes from it.
//   language-model   the page published no markup, so it was rendered to
//                    markdown and read by a model — including the title and
//                    summary, which are then the model's words and not the
//                    author's. Masses still come from the density tables.
export type ImportMethod = 'structured-data' | 'language-model'

// ImportedDraft is what /api/scrape returns: a recipe shape plus provenance
// and the review notes a curator must resolve before saving.
export interface ImportedDraft extends Omit<Recipe, 'canEdit' | 'ownerEmail' | 'createdAt' | 'updatedAt' | 'ingredients'> {
    ingredients: ParsedIngredient[]
    sourceUrl: string
    scrapedAt: string
    method: ImportMethod
    totalMassG: number
    needsReview: string[]
    curated: boolean
}

// importRecipe turns a public recipe URL into a draft. The draft is only ever a
// starting point — it is loaded into the editor for a person to check and
// correct, never saved directly.
//
// useModel forces the language-model path for a page whose markup is present but
// poor, which is common: sites publish an SEO blurb as the recipe description
// and leave the real method in prose the markup omits.
export function importRecipe(url: string, useModel = false): Promise<ImportedDraft> {
    return request<ImportedDraft>('/api/scrape', {
        method: 'POST', body: JSON.stringify({ url, useModel }),
    })
}

// measureQuantity asks what a stated amount of one ingredient weighs.
//
// The editor's quantity field takes any unit, and the browser can only finish
// the ones that are pure arithmetic — g, kg, oz, lb. A volume or a count is not
// a mass until you know the food ("1 cup" of flour and of water differ by half
// again), so those come here, where the same density and per-item tables an
// import uses answer it and say which row they answered from.
//
// An amount the tables cannot reach is not an error: it comes back with
// confidence `unknown`, a mass of 0 and a note naming what was missing. The
// caller must treat that as "leave the current figure alone and show why",
// never as a measurement of nothing.
export function measureQuantity(quantity: string, name: string): Promise<ParsedIngredient> {
    return request<ParsedIngredient>('/api/measure', {
        method: 'POST', body: JSON.stringify({ quantity, name }),
    })
}

// StepAmounts is what /api/step-uses returns: one entry per step, in order,
// plus what a person still has to check.
export interface StepAmounts {
    steps: { number: number; uses: StepUse[] }[]
    needsReview: string[]
}

// allocateStepUses works out which ingredients each step uses, and how much.
//
// This is the one figure on the site a model produces alone, because it is the
// one with no table behind it: "combine 1 cup (125 g) of the flour" against a
// 250 g flour line means half, and reading that is comprehension, not
// arithmetic. So it goes the same route every other model-produced figure goes
// — into the editor, in front of the author, before it is saved.
//
// It takes the lists rather than a recipe id so the editor can run it on a
// draft that has never been saved. Nothing is persisted; the caller saves.
export function allocateStepUses(
    ingredients: Ingredient[], steps: Step[],
): Promise<StepAmounts> {
    return request<StepAmounts>('/api/step-uses', {
        method: 'POST', body: JSON.stringify({ ingredients, steps }),
    })
}

// Confidence says how a mass was arrived at, worst to best:
//
//   unknown    nothing could be derived; the mass is 0 and needs weighing
//   model      a language model guessed it, with no table behind it
//   nominal    a pinch or a dash of something no table covers, taken as the
//              conventional amount — under half a gram either way
//   estimated  a published density or reference weight was applied
//   exact      the text already stated a mass
//
// The editor shows anything below `exact` differently, because the whole point
// of the site is that a recipe's masses are trustworthy.
export type Confidence = 'exact' | 'estimated' | 'nominal' | 'model' | 'unknown'

// ParsedIngredient is one line of a freeform parse. It carries how the mass was
// reached and what it was reached from, so the editor can show the difference
// between "USDA says a cup of this weighs 240 g" and "a model thinks so".
export interface ParsedIngredient extends Ingredient {
    raw: string
    method: string
    basis?: string
    source?: string
    confidence: Confidence
    note?: string
}

